import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Star, ShoppingCart, ChevronDown, Eye } from 'lucide-react';
import { getEyeglasses } from '../services/api';
import { useCart } from '../context/CartContext';
import Notification from '../components/Notification';
import { buildCategoriesFromProducts, getCategoryInfo } from '../utils/categoryUtils';
import { saveProductCardPosition, restoreProductCardPosition } from '../utils/scrollRestore';

const Eyeglasses = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const categoryFromUrl = searchParams.get('category') || 'all';
  const [selectedCategory, setSelectedCategory] = useState(categoryFromUrl);
  const [priceRange, setPriceRange] = useState('all');
  const [eyeglasses, setEyeglasses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isPriceOpen, setIsPriceOpen] = useState(false);
  const [notification, setNotification] = useState({ isVisible: false, message: '', type: 'success' });
  const { addToCart } = useCart();

  // Fetch data from Strapi API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const eyeglassesData = await getEyeglasses();
        
        console.log('Eyeglasses API Response:', eyeglassesData);
        
        // Transform Strapi data to match your existing structure
        const transformedEyeglasses = eyeglassesData.data.map(item => {
          // Get the first image and construct full URL
          let imageUrl = 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80'; // fallback
          
          if (item.Images && item.Images.length > 0) {
            const image = item.Images[0];
            // Use medium size for better performance, or fallback to original
            if (image.formats && image.formats.medium) {
              imageUrl = image.formats.medium.url.startsWith('http') 
                ? image.formats.medium.url 
                : `${process.env.REACT_APP_STRAPI_URL}${image.formats.medium.url}`;
            } else if (image.url) {
              imageUrl = image.url.startsWith('http') 
                ? image.url 
                : `${process.env.REACT_APP_STRAPI_URL}${image.url}`;
            }
          }
          
          const categoryInfo = getCategoryInfo(item.category);
          let categorySlug = categoryInfo.id;
          
          // Log products for debugging
          const productName = item.Name || item.name;
          if (productName && (productName.includes('MF-010') || productName === 'MF-010')) {
            console.log('MF-010 found:', {
              name: productName,
              id: item.id,
              category_slug: categorySlug,
              category_full: item.category,
              hasCategory: !!item.category
            });
          }
          
          // If no category, set to null so it shows in "All Styles" but not in specific category filters
          if (!categorySlug) {
            categorySlug = null;
          }
          
          const quantity = item.quantity !== undefined ? item.quantity : (item.inStock ? 999 : 0);
          
          return {
            id: item.id,
            name: item.Name || item.name,
            category: categorySlug,
            categoryName: categoryInfo.name,
            price: item.Price || item.price, // Store as number for calculations
            priceDisplay: `Rs. ${item.Price || item.price}`, // Formatted string for display
            originalPrice: item.originalPrice || item.OriginalPrice || null,
            image: imageUrl,
            description: item.Description || item.description,
            rating: item.rating || 5,
            isBestSeller: item.isBestseller || item.isBestSeller,
            isOnSale: item.isOnSale || false,
            discountPercentage: item.discountPercentage || 0,
            quantity: quantity,
            inStock: quantity > 0 && (item.inStock !== false)
          };
        });

        const transformedCategories = buildCategoriesFromProducts(transformedEyeglasses, 'All Styles');

        // Log all category slugs for debugging
        const categorySlugs = transformedEyeglasses.map(g => g.category);
        const uniqueCategories = [...new Set(categorySlugs)];
        console.log('All unique category slugs found:', uniqueCategories);
        console.log('Total products loaded:', transformedEyeglasses.length);
        console.log('Products by category:', uniqueCategories.map(cat => ({
          category: cat,
          count: categorySlugs.filter(c => c === cat).length
        })));

        setEyeglasses(transformedEyeglasses);
        setCategories(transformedCategories);
      } catch (err) {
        setError('Failed to load eyeglasses data');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Update selected category when URL changes
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category') || 'all';
    setSelectedCategory(categoryFromUrl);
  }, [searchParams]);

  const priceRanges = [
    { id: 'all', name: 'All Prices' },
    { id: 'under-2000', name: 'Under Rs. 2,000' },
    { id: '2000-3000', name: 'Rs. 2,000 - 3,000' },
    { id: '3000-4000', name: 'Rs. 3,000 - 4,000' },
    { id: 'over-4000', name: 'Over Rs. 4,000' }
  ];

  const getPriceValue = (priceNumber) => {
    return priceNumber; // Now it's already a number
  };

  const filteredGlasses = eyeglasses.filter(glass => {
    // Category filter
    // If "all" is selected, show all products (including those with null category)
    // Otherwise, match exact category slug (null category products only show in "all")
    const categoryMatch = selectedCategory === 'all' 
      ? true 
      : glass.category !== null && glass.category === selectedCategory;
    
    // Price filter
    const priceValue = getPriceValue(glass.price);
    let priceMatch = true;
    
    switch (priceRange) {
      case 'under-2000':
        priceMatch = priceValue < 2000;
        break;
      case '2000-3000':
        priceMatch = priceValue >= 2000 && priceValue <= 3000;
        break;
      case '3000-4000':
        priceMatch = priceValue >= 3000 && priceValue <= 4000;
        break;
      case 'over-4000':
        priceMatch = priceValue > 4000;
        break;
      default:
        priceMatch = true;
    }
    
    return categoryMatch && priceMatch;
  });

  const handleAddToCart = (glass) => {
    addToCart(glass, 1);
    setNotification({
      isVisible: true,
      message: `${glass.name} has been added to your cart successfully!`,
      type: 'success'
    });
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, isVisible: false }));
  };


  useEffect(() => {
    if (!loading) {
      restoreProductCardPosition();
    }
  }, [loading, filteredGlasses.length]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading eyeglasses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 text-lg">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 bg-primary text-white px-4 py-2 rounded-lg"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Eyeglasses - Mughal Ainak Mahal | Premium Frames in Mughalpura, Lahore</title>
        <meta name="description" content="Discover our premium collection of eyeglasses including metal, plastic, and designer frames. Best prices in Mughalpura, Lahore. Professional fitting and quality guarantee." />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-dark text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Premium Eyeglasses Collection
            </h1>
            <p className="text-xl max-w-2xl mx-auto opacity-90">
              Discover your perfect style with our extensive collection of high-quality frames
            </p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Filter Bar */}
          <div className="bg-gray-50 rounded-lg p-4 mb-8">
            {/* Mobile Layout */}
            <div className="block md:hidden">
              <div className="space-y-3">
                {/* Clear All Button */}
                <button
                  onClick={() => {
                    setSelectedCategory('all');
                    setPriceRange('all');
                    navigate('/eyeglasses');
                  }}
                  className="w-full bg-gray-200 text-secondary py-3 px-4 rounded-lg font-medium hover:bg-gray-300 transition-colors duration-200"
                >
                  Clear All Filters
                </button>

                {/* Categories Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                    className="w-full flex items-center justify-between bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors duration-200"
                  >
                    <span>{categories.find(cat => cat.id === selectedCategory)?.name || 'All Categories'}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  
                  {isCategoryOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      {categories.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => {
                            setSelectedCategory(category.id);
                            setIsCategoryOpen(false);
                            const url = category.id === 'all' ? '/eyeglasses' : `/eyeglasses?category=${category.id}`;
                            navigate(url);
                          }}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors duration-200 ${
                            selectedCategory === category.id ? 'bg-primary text-white' : 'text-gray-700'
                          }`}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Price Range Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setIsPriceOpen(!isPriceOpen)}
                    className="w-full flex items-center justify-between bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors duration-200"
                  >
                    <span>{priceRanges.find(range => range.id === priceRange)?.name || 'All Prices'}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  
                  {isPriceOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      {priceRanges.map((range) => (
                        <button
                          key={range.id}
                          onClick={() => {
                            setPriceRange(range.id);
                            setIsPriceOpen(false);
                          }}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors duration-200 ${
                            priceRange === range.id ? 'bg-primary text-white' : 'text-gray-700'
                          }`}
                        >
                          {range.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden md:flex items-center justify-between">
              {/* Clear All Button - Left */}
              <button
                onClick={() => {
                  setSelectedCategory('all');
                  setPriceRange('all');
                  navigate('/eyeglasses');
                }}
                className="bg-gray-200 text-secondary py-2 px-4 rounded-lg font-medium hover:bg-gray-300 transition-colors duration-200"
              >
                Clear All Filters
              </button>

              {/* Dropdown Filters - Right */}
              <div className="flex items-center space-x-4">
                {/* Categories Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                    className="flex items-center space-x-2 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors duration-200"
                  >
                    <span>{categories.find(cat => cat.id === selectedCategory)?.name || 'All Categories'}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  
                  {isCategoryOpen && (
                    <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      {categories.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => {
                            setSelectedCategory(category.id);
                            setIsCategoryOpen(false);
                            const url = category.id === 'all' ? '/eyeglasses' : `/eyeglasses?category=${category.id}`;
                            navigate(url);
                          }}
                          className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors duration-200 ${
                            selectedCategory === category.id ? 'bg-primary text-white' : 'text-gray-700'
                          }`}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Price Range Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setIsPriceOpen(!isPriceOpen)}
                    className="flex items-center space-x-2 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors duration-200"
                  >
                    <span>{priceRanges.find(range => range.id === priceRange)?.name || 'All Prices'}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  
                  {isPriceOpen && (
                    <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      {priceRanges.map((range) => (
                        <button
                          key={range.id}
                          onClick={() => {
                            setPriceRange(range.id);
                            setIsPriceOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors duration-200 ${
                            priceRange === range.id ? 'bg-primary text-white' : 'text-gray-700'
                          }`}
                        >
                          {range.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div>
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-8">
                {filteredGlasses.map((glass) => (
                                    <div
                    key={glass.id}
                    id={`product-card-${glass.id}`}
                    className="group flex h-full flex-col overflow-hidden rounded-lg bg-white shadow-md transition duration-300 hover:-translate-y-1 hover:shadow-xl md:shadow-lg"
                  >
                    <Link
                      to={`/product/${glass.id}`}
                      onClick={() => saveProductCardPosition(glass.id)}
                      className="block"
                    >
                      <div className="relative aspect-[1.08/1] overflow-hidden bg-white md:aspect-[4/3]">
                        <img
                          src={glass.image}
                          alt={glass.name}
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80';
                          }}
                        />
                        {glass.isOnSale && glass.discountPercentage > 0 && (
                          <div className="absolute left-px top-px bg-red-500 px-2 py-0.5 text-[11px] font-semibold text-white rounded-tl-md rounded-br-md sm:px-3 sm:py-1 sm:text-sm">
                            {glass.discountPercentage}% OFF
                          </div>
                        )}
                        {glass.isBestSeller && (
                          <div className="absolute right-px top-px bg-yellow-500 px-1.5 py-0.5 text-[10px] font-semibold text-white rounded-tr-md rounded-bl-md sm:px-3 sm:py-1 sm:text-sm">
                            Best Seller
                          </div>
                        )}
                      </div>
                    </Link>

                    <div className="flex flex-1 flex-col px-2 pb-2 pt-1.5 text-center md:px-5 md:pb-5 md:pt-3">

                      <Link
                        to={`/product/${glass.id}`}
                        onClick={() => saveProductCardPosition(glass.id)}
                        className="block"
                      >
                        <h3 className="line-clamp-2 min-h-[24px] text-[12px] font-bold uppercase leading-tight tracking-[0.04em] text-secondary transition hover:text-primary sm:text-sm md:min-h-[38px] md:text-base md:tracking-[0.08em]">
                          {glass.name}
                        </h3>
                      </Link>

                      <div className="mt-0.5 flex min-h-[16px] flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 text-xs md:mt-1.5 md:min-h-[22px] md:gap-2 md:text-base">
                        {glass.originalPrice && glass.originalPrice > glass.price && (
                          <span className="text-gray-500 line-through">Rs. {glass.originalPrice.toLocaleString()}</span>
                        )}
                        <span className="font-bold text-primary">{glass.priceDisplay}</span>
                      </div>

                      <div className="mt-1 flex min-h-[14px] items-center justify-center gap-1 text-[10px] leading-none text-gray-600 md:mt-2 md:min-h-[18px] md:text-sm">
                        <span className="tracking-tight text-yellow-400">★★★★★</span>
                        <span>{glass.rating || 5} reviews</span>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleAddToCart(glass)}
                        className="mt-1 w-full rounded-md bg-primary px-2 py-2 text-xs font-bold uppercase tracking-[0.06em] text-white transition hover:bg-primary-dark md:mt-2 md:px-4 md:py-3 md:text-base md:tracking-[0.18em]"
                      >
                        Add to Cart
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {filteredGlasses.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">No products found in this category.</p>
                </div>
              )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-secondary mb-4">
              Why Choose Our Eyeglasses?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We offer the best quality frames with professional fitting services
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Eye className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-secondary">Professional Fitting</h3>
              <p className="text-gray-600">Expert opticians ensure perfect fit and comfort for your frames</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-All2 text-secondary">Quality Guarantee</h3>
              <p className="text-gray-600">All frames come with warranty and quality assurance</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingCart className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-secondary">Easy Returns</h3>
              <p className="text-gray-600">30-day return policy if you're not completely satisfied</p>
            </div>
          </div>
        </div>
      </section>

      {/* Notification */}
      <Notification 
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={handleCloseNotification}
      />
    </div>
  );
};

export default Eyeglasses;
